/* PDP main — variant selection, price swap, add-to-cart, delivery check, share,
   price breakup toggle, size expand.

   Delegates to window.CaratKinCartDrawer for cart drawer. Uses the shared
   whatsapp-form.js handler (POST to /apps/cart-kin/whatsapp/message with
   surface 'product') — the WhatsApp link is rendered as a form-less anchor
   that we intercept.
*/

(() => {
  if (window.CaratKinPdpMain) return;
  window.CaratKinPdpMain = true;

  const t = window.CaratKinStrings || {};
  const root = () => window.Shopify?.routes?.root || '/';

  /* ---- Variant data ----------------------------------------------------- */

  let variants = [];
  const dataEl = document.querySelector('[data-pdp-variants]');
  if (dataEl) {
    try { variants = JSON.parse(dataEl.textContent); } catch { /* empty */ }
  }

  function findVariant(selectedOptions) {
    return variants.find((v) =>
      v.options.every((opt, i) => !selectedOptions[i] || opt === selectedOptions[i])
    );
  }

  /* ---- Option selection ------------------------------------------------- */

  function getSelectedOptions() {
    const options = [];
    document.querySelectorAll('[data-option-index]').forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const selected = group.querySelector('.is-selected');
      if (selected) options[idx] = selected.dataset.optionValue;
    });
    return options;
  }

  function updateVariant() {
    const selected = getSelectedOptions();
    const variant = findVariant(selected);
    if (!variant) return;

    // Price
    const priceEl = document.querySelector('[data-pdp-price]');
    if (priceEl) {
      const strong = priceEl.querySelector('strong');
      const s = priceEl.querySelector('s');
      const em = priceEl.querySelector('em');
      if (strong) strong.textContent = '₹' + Number(variant.price / 100).toLocaleString('en-IN');
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const off = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        if (s) { s.textContent = '₹' + Number(variant.compare_at_price / 100).toLocaleString('en-IN'); s.hidden = false; }
        if (em) { em.textContent = off + '% Off'; em.hidden = off === 0; }
      } else {
        if (s) s.hidden = true;
        if (em) em.hidden = true;
      }
    }

    // Cart button variant id
    const cartBtn = document.querySelector('[data-pdp-cart]');
    if (cartBtn) {
      cartBtn.dataset.variantId = variant.id;
      cartBtn.disabled = !variant.available;
    }

    // Gallery image swap
    if (variant.featured_image) {
      document.dispatchEvent(new CustomEvent('pdp:variant-change', {
        detail: { imageUrl: variant.featured_image.src }
      }));
    }

    // Description-tab specifications: Metal, Gold purity and Gold weight follow
    // the karat, so they must move with the variant. They are rendered
    // server-side from the composition metaobjects, so re-render that section.
    refreshSpecs(variant.id);
  }

  // Option button clicks (Metal, Purity, Size when variant-based)
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-option-value]');
    if (!btn) return;
    const group = btn.closest('[data-option-index], [data-non-variant-size]');
    if (!group) return;

    // For non-variant sizes, just toggle selection class
    if (group.dataset.nonVariantSize === 'true') {
      group.querySelectorAll('[data-option-value]').forEach((b) => b.classList.toggle('is-selected', b === btn));
      return;
    }

    group.querySelectorAll('[data-option-value]').forEach((b) => b.classList.toggle('is-selected', b === btn));
    updateVariant();
  });

  /* ---- Specifications refresh ------------------------------------------
     The Product Specifications grid lives in the info-tabs section and is
     rendered from the selected variant's karat. Shopify's section rendering
     returns just that section, so fetch it and swap the grid in place.
  */

  const specsSeed = document.querySelector('[data-pdp-specs]');
  const specsSectionId = specsSeed
    ? (specsSeed.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || null)
    : null;
  let specsInFlight = 0;

  async function refreshSpecs(variantId) {
    if (!specsSectionId || !variantId) return;
    const ticket = ++specsInFlight;
    try {
      const url =
        window.location.pathname +
        '?variant=' + encodeURIComponent(variantId) +
        '&section_id=' + encodeURIComponent(specsSectionId);
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-pdp-specs]');
      const cur = document.querySelector('[data-pdp-specs]');
      // A slower earlier request must not overwrite a newer one.
      if (!next || !cur || ticket !== specsInFlight) return;
      cur.innerHTML = next.innerHTML;
    } catch {
      /* leave the existing specs rather than blanking the grid */
    }
  }

  /* ---- Size expand ------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-show-sizes]');
    if (!btn) return;
    const container = btn.closest('[data-size-options]');
    if (!container) return;
    const expanded = container.classList.toggle('is-expanded');
    btn.textContent = expanded ? 'See less' : 'See more';
    btn.setAttribute('aria-expanded', String(expanded));
  });

  /* ---- Add to cart ------------------------------------------------------ */

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-pdp-cart]');
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const items = [{ id: Number(variantId), quantity: 1 }];
    // Guests sign in first; auth-gate.js adds this line once they are back.
    if (window.CaratKinAuthGate?.intercept({ kind: 'cart', items }, () => btn.click())) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = t.adding || 'Adding…';

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Failed');

      btn.classList.add('is-added');
      btn.textContent = t.added || 'Added to cart';

      await window.CaratKinCartDrawer?.refresh();
      window.CaratKinCartDrawer?.open();
    } catch (err) {
      console.error(err);
      btn.textContent = t.addFailed || 'Could not add — try again';
    } finally {
      btn.disabled = false;
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = original;
      }, 2000);
    }
  });

  /* ---- Complete the Look cart buttons ----------------------------------- */

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-look-cart]');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const items = [{ id: Number(variantId), quantity: 1 }];
    if (window.CaratKinAuthGate?.intercept({ kind: 'cart', items }, () => btn.click())) return;

    btn.disabled = true;
    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Failed');

      const label = btn.querySelector('span');
      if (label) label.textContent = 'Added to cart';
      else btn.textContent = 'Added to cart';
      btn.classList.add('is-added');

      await window.CaratKinCartDrawer?.refresh();
      window.CaratKinCartDrawer?.open();
    } catch (err) {
      console.error(err);
    } finally {
      btn.disabled = false;
    }
  });

  /* ---- Delivery & try at home ------------------------------------------
     Sends the PIN to our own endpoint (Sequel247 sits behind it, server-side,
     so the API token never reaches the browser). On a serviceable PIN the two
     hint lines are rewritten in place: the location line offers "Try at home"
     with a WhatsApp "book your slot now." link, and the truck line shows the day estimate
     (Sequel transit days + the made-to-order buffer).
  */

  const pinForm = document.querySelector('[data-pin-form]');
  const pinResult = document.querySelector('[data-pin-result]');

  // Rewrite the text after an icon in a hint line, keeping the icon itself.
  function setHint(iconSelector, build) {
    const icon = document.querySelector(iconSelector);
    const line = icon && icon.closest('p');
    if (!line) return;
    while (icon.nextSibling) icon.nextSibling.remove();
    build(line);
  }

  if (pinForm && pinResult) {
    pinForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = pinForm.querySelector('input');
      const pincode = input.value.trim();
      if (!/^\d{6}$/.test(pincode)) {
        pinResult.textContent = 'Please enter a valid 6-digit PIN code.';
        pinResult.classList.add('has-result');
        pinResult.style.color = '#c0534a';
        return;
      }

      const submit = pinForm.querySelector('button[type="submit"]');
      const productTitle = (document.getElementById('pdp-title')?.textContent || '').trim();
      submit.disabled = true;
      pinResult.textContent = 'Checking…';
      pinResult.classList.add('has-result');
      pinResult.style.color = '#555';

      try {
        const res = await fetch(
          `/apps/cart-kin/delivery?pincode=${encodeURIComponent(pincode)}` +
          `&product=${encodeURIComponent(productTitle)}`,
          { headers: { Accept: 'application/json' } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed');

        if (!data.serviceable) {
          pinResult.textContent = 'Sorry, we do not deliver to this PIN code yet.';
          pinResult.style.color = '#c0534a';
          setHint('.pdp-location', (line) => line.append(' Try at home is not available at this PIN code'));
          setHint('.pdp-truck', (line) => line.append(' Delivery is not available at this PIN code'));
        } else {
          pinResult.textContent = '';
          pinResult.classList.remove('has-result');

          setHint('.pdp-location', (line) => {
            line.append(' Try at home available ');
            if (data.whatsappLink) {
              const a = document.createElement('a');
              a.href = data.whatsappLink;
              a.target = '_blank';
              a.rel = 'noopener';
              a.className = 'pdp-try-link';
              a.textContent = 'book your slot now.';
              a.style.color = '#174d34';
              a.style.fontWeight = '600';
              a.style.textDecoration = 'underline';
              a.style.textUnderlineOffset = '2px';
              line.append(a);
            }
          });

          setHint('.pdp-truck', (line) => {
            if (!data.days) {
              line.append(' Delivery available for this PIN code');
              return;
            }
            // The estimate is styled like the booking link above: same green, same weight.
            line.append(' Will deliver in ');
            const days = document.createElement('strong');
            days.className = 'pdp-delivery-days';
            // Courier transit days plus the time to make or dispatch the piece
            // (data-lead-days, set in pdp-main.liquid from "Made to order").
            const lead = Number(pinForm.dataset.leadDays) || 0;
            const transit = Number(data.days);
            const total = Number.isFinite(transit) ? transit + lead : data.days;
            days.textContent = `approx. ${total} ${total === 1 ? 'day' : 'days'}.`;
            days.style.color = '#174d34';
            days.style.fontWeight = '600';
            line.append(days);
          });
        }
      } catch {
        pinResult.textContent = 'Delivery check is temporarily unavailable. Please try again shortly.';
        pinResult.style.color = '#c0534a';
      } finally {
        submit.disabled = false;
      }
    });
  }

  /* "Ask about this piece" is a plain WhatsApp link built in pdp-main.liquid. */

  /* ---- Share ------------------------------------------------------------- */

  document.querySelector('[data-share-product]')?.addEventListener('click', async () => {
    const data = { title: document.title, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else await navigator.clipboard.writeText(window.location.href);
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    }
  });

  /* ---- Sticky gallery pin point (desktop) -------------------------------
     The gallery is position: sticky (product-detail.css). Below the 112px
     header it pins at 124px; if it is taller than the room left on screen, it
     pins higher (negative top) so its bottom edge, with the thumbnails, sits
     16px above the bottom of the window instead of running off screen. */

  const gallery = document.querySelector('.pdp-gallery');
  if (gallery && window.ResizeObserver) {
    const pin = () => {
      const top = Math.min(124, window.innerHeight - gallery.offsetHeight - 16);
      gallery.style.setProperty('--pdp-sticky-top', `${top}px`);
    };
    new ResizeObserver(pin).observe(gallery);
    window.addEventListener('resize', pin);
    pin();
  }

  /* ---- Price breakup toggle --------------------------------------------- */

  document.querySelector('[data-breakup-toggle]')?.addEventListener('click', () => {
    const panel = document.querySelector('[data-pdp-breakup]');
    if (panel) panel.hidden = !panel.hidden;
  });
})();
